import { useMemo, useState } from 'react';
import type { SywComponent } from '../hooks/useSywData';
import type { DeviceCombo } from '../store/wizardStore';
import {
  teclasOf,
  friendlyName,
  isTomada,
  isEspelho,
  isVpro,
  boxesOf,
  colorsOf,
  isColorable,
  swatchHex,
  isCustomOrderEspelho,
  closestEspelhoSuggestion,
  extraKeyNote,
} from '../lib/catalogModel';
import { pricingOf, usePricing, mergePricing, formatPrice, type SkuPricing } from '../services/pricing';

// Guided "adicionar aparelho" popup (Fase A):
//   1. tipo/botão → 2. nº de caixas de aparelhagem → 3. cor → 4. módulo
// The result is a DeviceCombo (módulo + espelho) saved into the division.
// Fase B adds: toggle "Guiado" | "Explorar catálogo" with search, filter chips,
// sort, and colour-variant swatches.

type Kind = 'interruptor' | 'tomada';
type Boxes = 1 | 2 | 3 | 4;
type WizardMode = 'guided' | 'browse';

const STEP_TITLES = [
  'O que queres adicionar?',
  'Quantas caixas de aparelhagem?',
  'Qual a cor?',
  'Escolhe o módulo',
] as const;

let comboSeq = 0;
function comboId(): string {
  comboSeq += 1;
  return `combo-${Date.now().toString(36)}-${comboSeq}`;
}

// Rank candidate rows: exact colour match first, then vPRO, then name.
function rankCandidates(
  rows: SywComponent[],
  color: string | null,
): SywComponent[] {
  return [...rows].sort((a, b) => {
    const colorRank = (c: SywComponent) => (color && c.colorName === color ? 0 : 1);
    const vproRank = (c: SywComponent) => (isVpro(c) ? 0 : 1);
    return (
      colorRank(a) - colorRank(b) ||
      vproRank(a) - vproRank(b) ||
      a.name.localeCompare(b.name)
    );
  });
}

function CandidateRow({
  comp,
  remote,
  selected,
  onSelect,
}: {
  comp: SywComponent;
  remote?: SkuPricing;
  selected: boolean;
  onSelect: () => void;
}) {
  const pricing = mergePricing(pricingOf(comp), remote);
  const isCustom = isCustomOrderEspelho(comp, pricing.priceEur, remote?.isCustomOrder);
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={[
          'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors',
          selected ? 'bg-blue-50' : 'hover:bg-gray-50',
        ].join(' ')}
      >
        {comp.imageUrl ? (
          <img
            src={comp.imageUrl}
            alt=""
            loading="lazy"
            className="h-10 w-10 shrink-0 rounded-lg border border-smartify-line bg-white object-contain"
          />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-smartify-line bg-smartify-paper">
            🔌
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium leading-snug text-smartify-ink">
            {friendlyName(comp)}
          </p>
          {isCustom ? (
            <div className="mt-0.5 space-y-0.5">
              <p className="text-xs text-smartify-muted">
                {formatPrice(pricing.priceEur)}
                {comp.colorName ? ` · ${comp.colorName}` : ''}
                {' · '}
                <span className="text-amber-600 font-medium">~5-8 semanas</span>
              </p>
              <span className="inline-block rounded bg-amber-50 px-1 py-px text-[10px] font-semibold text-amber-700 leading-tight">
                Personalizado por encomenda
              </span>
            </div>
          ) : (
            <p className="text-xs text-smartify-muted">
              {formatPrice(pricing.priceEur)}
              {comp.colorName ? ` · ${comp.colorName}` : ''}
            </p>
          )}
        </div>
        <span
          className={[
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold',
            selected
              ? 'border-smartify-brand bg-smartify-brand text-white'
              : 'border-smartify-line bg-white text-transparent',
          ].join(' ')}
          aria-hidden
        >
          ✓
        </span>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Browse mode — group by productId (Shopify) or individual SKU (SYW fallback)
// ---------------------------------------------------------------------------

type ProductGroup = {
  /** Representative component (first variant, or sole SKU) */
  rep: SywComponent;
  /** All variants (same productId or single-item array) */
  variants: SywComponent[];
};

function groupByProduct(catalog: SywComponent[]): ProductGroup[] {
  const map = new Map<string, SywComponent[]>();
  for (const c of catalog) {
    const key = c.productId ?? `sku:${c.sku}`;
    const arr = map.get(key) ?? [];
    arr.push(c);
    map.set(key, arr);
  }
  return [...map.values()].map((variants) => ({
    rep: variants[0]!,
    variants,
  }));
}

/** Derive category chips from the `type` field. */
function categoryChips(catalog: SywComponent[]): string[] {
  const types = [...new Set(catalog.map((c) => c.type).filter(Boolean))].sort();
  return types;
}

/** Human label for a raw catalog `type` (Shopify productType comes in EN/CAPS,
 * e.g. "PRODUCT" — never show that to the customer). */
function typeLabel(t: string): string {
  const known: Record<string, string> = { PRODUCT: 'Produtos', SERVICE: 'Serviços' };
  const upper = t.toUpperCase();
  if (known[upper]) return known[upper];
  // Title-case anything else that arrives ALL-CAPS from the source.
  return t === upper ? t.charAt(0) + t.slice(1).toLowerCase() : t;
}

type SortKey = 'az' | 'price-asc' | 'price-desc';

export function BrowseMode({
  catalog,
  onAdd,
}: {
  catalog: SywComponent[];
  onAdd: (comp: SywComponent) => void;
}) {
  const [query, setQuery] = useState('');
  const [filterType, setFilterType] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('az');
  // Track selected colour variant per productId key
  const [selectedVariant, setSelectedVariant] = useState<Record<string, string>>({});

  const chips = useMemo(() => categoryChips(catalog.filter((c) => c.active)), [catalog]);

  const filtered = useMemo(() => {
    let rows = catalog.filter((c) => c.active);
    if (filterType) rows = rows.filter((c) => c.type === filterType);
    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.sku.toLowerCase().includes(q) ||
          friendlyName(c).toLowerCase().includes(q),
      );
    }
    return rows;
  }, [catalog, filterType, query]);

  const groups = useMemo(() => {
    const gs = groupByProduct(filtered);
    return [...gs].sort((a, b) => {
      const ra = a.rep;
      const rb = b.rep;
      if (sort === 'az') return friendlyName(ra).localeCompare(friendlyName(rb));
      if (sort === 'price-asc') return ra.priceEur - rb.priceEur;
      return rb.priceEur - ra.priceEur;
    });
  }, [filtered, sort]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Search */}
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-smartify-muted">
          🔍
        </span>
        <input
          type="search"
          placeholder="Pesquisar produtos…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-smartify border border-smartify-line py-2 pl-9 pr-3 text-sm focus:border-smartify-brand focus:outline-none"
        />
      </div>

      {/* Filter chips — with a single type there is nothing to filter */}
      {chips.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setFilterType(null)}
            className={[
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              filterType === null
                ? 'border-smartify-brand bg-smartify-brand text-white'
                : 'border-smartify-line bg-white text-smartify-muted hover:border-smartify-accent',
            ].join(' ')}
          >
            Todos
          </button>
          {chips.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setFilterType(filterType === t ? null : t)}
              className={[
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                filterType === t
                  ? 'border-smartify-brand bg-smartify-brand text-white'
                  : 'border-smartify-line bg-white text-smartify-muted hover:border-smartify-accent',
              ].join(' ')}
            >
              {typeLabel(t)}
            </button>
          ))}
        </div>
      )}

      {/* Sort */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-smartify-muted">Ordenar:</span>
        {(['az', 'price-asc', 'price-desc'] as const).map((s) => {
          const label = s === 'az' ? 'A-Z' : s === 'price-asc' ? 'Preço ↑' : 'Preço ↓';
          return (
            <button
              key={s}
              type="button"
              onClick={() => setSort(s)}
              className={[
                'rounded border px-2 py-0.5 text-xs transition-colors',
                sort === s
                  ? 'border-smartify-brand bg-smartify-brand text-white'
                  : 'border-smartify-line bg-white text-smartify-muted hover:border-smartify-accent',
              ].join(' ')}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Product list */}
      {groups.length === 0 ? (
        <p className="rounded-smartify border border-smartify-line bg-smartify-paper p-4 text-center text-sm text-smartify-muted">
          Nenhum produto encontrado.
        </p>
      ) : (
        <ul className="divide-y divide-smartify-line overflow-hidden rounded-smartify border border-smartify-line">
          {groups.map(({ rep, variants }) => {
            const groupKey = rep.productId ?? `sku:${rep.sku}`;
            const hasVariants = variants.length > 1 && variants.some((v) => v.colorName);
            const colorVariants = hasVariants
              ? variants.filter((v) => v.colorName)
              : [];
            const activeVariantSku = selectedVariant[groupKey] ?? rep.sku;
            const activeComp =
              variants.find((v) => v.sku === activeVariantSku) ?? rep;
            const pricing = pricingOf(activeComp);

            return (
              <li key={groupKey} className="flex items-start gap-3 p-3">
                {/* Thumbnail */}
                {activeComp.imageUrl ? (
                  <img
                    src={activeComp.imageUrl}
                    alt=""
                    loading="lazy"
                    className="h-12 w-12 shrink-0 rounded-lg border border-smartify-line bg-white object-contain"
                  />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-smartify-line bg-smartify-paper text-xl">
                    🔌
                  </div>
                )}

                {/* Info + swatches */}
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm font-medium leading-snug text-smartify-ink">
                    {friendlyName(activeComp)}
                  </p>
                  <p className="mt-0.5 text-xs text-smartify-muted">
                    {formatPrice(pricing.priceEur)}
                    {activeComp.colorName ? ` · ${activeComp.colorName}` : ''}
                  </p>

                  {/* Colour variant swatches */}
                  {colorVariants.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {colorVariants.map((v) => (
                        <button
                          key={v.sku}
                          type="button"
                          title={v.colorName ?? v.sku}
                          aria-label={`Cor: ${v.colorName ?? v.sku}`}
                          aria-pressed={activeVariantSku === v.sku}
                          onClick={() =>
                            setSelectedVariant((prev) => ({
                              ...prev,
                              [groupKey]: v.sku,
                            }))
                          }
                          className={[
                            'h-5 w-5 rounded-full border-2 transition-transform hover:scale-110',
                            activeVariantSku === v.sku
                              ? 'border-smartify-brand ring-1 ring-smartify-brand ring-offset-1'
                              : 'border-smartify-line',
                          ].join(' ')}
                          style={{ backgroundColor: swatchHex(v.colorName ?? '') }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Add button */}
                <button
                  type="button"
                  onClick={() => onAdd(activeComp)}
                  className="shrink-0 rounded-smartify bg-smartify-brand px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
                >
                  + Adicionar
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main wizard component
// ---------------------------------------------------------------------------

export function AddDeviceWizard({
  catalog,
  onSave,
  onClose,
}: {
  catalog: SywComponent[];
  onSave: (combo: DeviceCombo) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<WizardMode>('guided');

  // Guided mode state
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [kind, setKind] = useState<Kind | null>(null);
  const [teclas, setTeclas] = useState<number | null>(null);
  const [boxes, setBoxes] = useState<Boxes | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [moduleSku, setModuleSku] = useState<string | null>(null);
  const [espelhoSku, setEspelhoSku] = useState<string | null>(null);

  const colors = useMemo(() => colorsOf(catalog.filter(isColorable)), [catalog]);

  const moduleCandidates = useMemo(() => {
    if (!kind) return [];
    const rows =
      kind === 'tomada'
        ? catalog.filter((c) => c.active && isTomada(c))
        : catalog.filter((c) => c.active && !isEspelho(c) && teclasOf(c) === teclas);
    return rankCandidates(rows, color);
  }, [catalog, kind, teclas, color]);

  const espelhoCandidates = useMemo(() => {
    const exact = catalog.filter((c) => c.active && boxesOf(c) === boxes);
    const rows = exact.length > 0 ? exact : catalog.filter((c) => c.active && isEspelho(c));
    return rankCandidates(rows, color);
  }, [catalog, boxes, color]);

  // Remote pricing for espelho candidates — gives isCustomOrder flag + formula price.
  const espelhoSkus = useMemo(
    () => espelhoCandidates.map((c) => c.sku),
    [espelhoCandidates],
  );
  const espelhoRemotePricing = usePricing(espelhoSkus);

  // Top candidate is custom-order when the remote flag says so (or null price as fallback).
  const topEspelho = espelhoCandidates[0];
  const topEspelhoRemote = topEspelho ? espelhoRemotePricing[topEspelho.sku] : undefined;
  const topEspelhoMerged = topEspelho ? mergePricing(pricingOf(topEspelho), topEspelhoRemote) : null;
  const topEspelhoIsCustomOrder = topEspelho
    ? isCustomOrderEspelho(topEspelho, topEspelhoMerged?.priceEur ?? null, topEspelhoRemote?.isCustomOrder)
    : false;

  const espelhoSuggestion = useMemo(
    () =>
      topEspelhoIsCustomOrder
        ? closestEspelhoSuggestion(
            boxes,
            color,
            catalog,
            (sku) => espelhoRemotePricing[sku]?.isCustomOrder ?? false,
          )
        : null,
    [topEspelhoIsCustomOrder, boxes, color, catalog, espelhoRemotePricing],
  );

  function enterStep4() {
    // Pre-pick the best-ranked candidates; the customer can change them.
    setModuleSku(moduleCandidates[0]?.sku ?? null);
    setEspelhoSku(espelhoCandidates[0]?.sku ?? null);
    setStep(3);
  }

  const canContinue =
    step === 0 ? kind !== null && (kind === 'tomada' || teclas !== null)
    : step === 1 ? boxes !== null
    : step === 2 ? true // cor é opcional ("sem preferência")
    : moduleSku !== null;

  function handleSave() {
    if (!kind || !boxes || !moduleSku) return;
    const skus = [moduleSku, ...(espelhoSku && espelhoSku !== moduleSku ? [espelhoSku] : [])];
    const kindLabel =
      kind === 'tomada'
        ? 'Tomada'
        : `Interruptor ${teclas} ${teclas === 1 ? 'tecla' : 'teclas'}`;
    onSave({
      id: comboId(),
      kind,
      teclas: kind === 'tomada' ? null : teclas,
      boxes,
      color,
      label: `${kindLabel} · ${boxes} ${boxes === 1 ? 'caixa' : 'caixas'}${color ? ` · ${color}` : ''}`,
      skus,
      qty: 1,
    });
    onClose();
  }

  // Browse mode: add a single component as a simple DeviceCombo
  function handleBrowseAdd(comp: SywComponent) {
    const inferredKind: Kind = isTomada(comp) ? 'tomada' : 'interruptor';
    const inferredTeclas = isTomada(comp) ? null : (teclasOf(comp) ?? 1);
    onSave({
      id: comboId(),
      kind: inferredKind,
      teclas: inferredTeclas,
      boxes: 1,
      color: comp.colorName ?? null,
      label: `${friendlyName(comp)}${comp.colorName ? ` · ${comp.colorName}` : ''}`,
      skus: [comp.sku],
      qty: 1,
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Adicionar aparelho"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full flex-col rounded-t-2xl bg-white sm:max-w-md sm:rounded-smartify"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-smartify-line px-4 py-3">
          {mode === 'guided' && step > 0 && (
            <button
              type="button"
              onClick={() => setStep((s) => (s - 1) as 0 | 1 | 2 | 3)}
              aria-label="Passo anterior"
              className="-ml-1 p-1 text-lg leading-none text-smartify-muted hover:text-smartify-ink"
            >
              ←
            </button>
          )}
          <div className="min-w-0 flex-1">
            {mode === 'guided' && (
              <p className="text-xs text-smartify-muted">Passo {step + 1} de 4</p>
            )}
            <h2 className="truncate text-sm font-semibold text-smartify-ink">
              {mode === 'guided' ? STEP_TITLES[step] : 'Explorar catálogo'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-1 text-lg leading-none text-smartify-muted hover:text-smartify-ink"
          >
            ✕
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex border-b border-smartify-line">
          <button
            type="button"
            onClick={() => setMode('guided')}
            className={[
              'flex-1 py-2 text-xs font-semibold transition-colors',
              mode === 'guided'
                ? 'border-b-2 border-smartify-brand text-smartify-brand'
                : 'text-smartify-muted hover:text-smartify-ink',
            ].join(' ')}
          >
            Guiado
          </button>
          <button
            type="button"
            onClick={() => setMode('browse')}
            className={[
              'flex-1 py-2 text-xs font-semibold transition-colors',
              mode === 'browse'
                ? 'border-b-2 border-smartify-brand text-smartify-brand'
                : 'text-smartify-muted hover:text-smartify-ink',
            ].join(' ')}
          >
            Explorar catálogo
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {mode === 'browse' ? (
            <BrowseMode catalog={catalog} onAdd={handleBrowseAdd} />
          ) : (
            <>
              {step === 0 && (
                <div className="space-y-2">
                  {[1, 2, 3].map((n) => {
                    const active = kind === 'interruptor' && teclas === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        aria-pressed={active}
                        onClick={() => {
                          setKind('interruptor');
                          setTeclas(n);
                        }}
                        className={[
                          'flex w-full items-center gap-3 rounded-smartify border p-3 text-left transition-colors',
                          active
                            ? 'border-smartify-brand bg-blue-50'
                            : 'border-smartify-line bg-white hover:border-smartify-accent',
                        ].join(' ')}
                      >
                        <span className="text-xl" aria-hidden>💡</span>
                        <span>
                          <span className="block text-sm font-medium text-smartify-ink">
                            Interruptor de {n} {n === 1 ? 'tecla' : 'teclas'}
                          </span>
                          <span className="block text-xs text-smartify-muted">
                            {n === 1 ? 'Um botão na parede' : `${n} botões no mesmo espelho`}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    aria-pressed={kind === 'tomada'}
                    onClick={() => {
                      setKind('tomada');
                      setTeclas(null);
                    }}
                    className={[
                      'flex w-full items-center gap-3 rounded-smartify border p-3 text-left transition-colors',
                      kind === 'tomada'
                        ? 'border-smartify-brand bg-blue-50'
                        : 'border-smartify-line bg-white hover:border-smartify-accent',
                    ].join(' ')}
                  >
                    <span className="text-xl" aria-hidden>🔌</span>
                    <span>
                      <span className="block text-sm font-medium text-smartify-ink">Tomada</span>
                      <span className="block text-xs text-smartify-muted">
                        Tomada inteligente de parede
                      </span>
                    </span>
                  </button>
                </div>
              )}

              {step === 1 && (
                <div>
                  <p className="mb-3 text-sm text-smartify-muted">
                    Conta os "buracos" na parede onde o espelho vai encaixar.
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {([1, 2, 3, 4] as const).map((n) => {
                      const active = boxes === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          aria-pressed={active}
                          onClick={() => setBoxes(n)}
                          className={[
                            'flex flex-col items-center rounded-smartify border p-3 transition-colors',
                            active
                              ? 'border-smartify-brand bg-smartify-brand text-white'
                              : 'border-smartify-line bg-white text-smartify-ink hover:border-smartify-accent',
                          ].join(' ')}
                        >
                          <span className="text-2xl font-bold">{n}</span>
                          <span className={['text-[10px]', active ? 'text-white/80' : 'text-smartify-muted'].join(' ')}>
                            {n === 1 ? 'caixa' : 'caixas'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-2">
                  {colors.length === 0 && (
                    <p className="rounded-smartify border border-smartify-line bg-smartify-paper p-3 text-sm text-smartify-muted">
                      Sem variantes de cor disponíveis de momento — podes continuar.
                    </p>
                  )}
                  {colors.map((c) => {
                    const active = color === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setColor(active ? null : c)}
                        className={[
                          'flex w-full items-center gap-3 rounded-smartify border p-3 text-left transition-colors',
                          active
                            ? 'border-smartify-brand bg-blue-50'
                            : 'border-smartify-line bg-white hover:border-smartify-accent',
                        ].join(' ')}
                      >
                        <span
                          className="h-7 w-7 shrink-0 rounded-full border border-smartify-line"
                          style={{ backgroundColor: swatchHex(c) }}
                          aria-hidden
                        />
                        <span className="text-sm font-medium text-smartify-ink">{c}</span>
                        {active && <span className="ml-auto text-smartify-brand" aria-hidden>✓</span>}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    aria-pressed={color === null}
                    onClick={() => setColor(null)}
                    className={[
                      'w-full rounded-smartify border p-3 text-left text-sm transition-colors',
                      color === null
                        ? 'border-smartify-brand bg-blue-50 font-medium text-smartify-ink'
                        : 'border-smartify-line bg-white text-smartify-muted hover:border-smartify-accent',
                    ].join(' ')}
                  >
                    Sem preferência
                  </button>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
                      Módulo
                    </h3>
                    {moduleCandidates.length === 0 ? (
                      <p className="rounded-smartify border border-smartify-line bg-smartify-paper p-3 text-sm text-smartify-muted">
                        Nenhum módulo correspondente no catálogo. Ajusta os passos
                        anteriores ou adiciona pela lista.
                      </p>
                    ) : (
                      <ul className="divide-y divide-smartify-line overflow-hidden rounded-smartify border border-smartify-line">
                        {moduleCandidates.slice(0, 6).map((comp) => (
                          <CandidateRow
                            key={comp.sku}
                            comp={comp}
                            selected={moduleSku === comp.sku}
                            onSelect={() => setModuleSku(comp.sku)}
                          />
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
                      Espelho ({boxes} {boxes === 1 ? 'caixa' : 'caixas'})
                    </h3>
                    {espelhoCandidates.length === 0 ? (
                      <p className="rounded-smartify border border-smartify-line bg-smartify-paper p-3 text-sm text-smartify-muted">
                        Sem espelhos no catálogo — o módulo é guardado sozinho.
                      </p>
                    ) : (
                      <>
                        <ul className="divide-y divide-smartify-line overflow-hidden rounded-smartify border border-smartify-line">
                          {espelhoCandidates.slice(0, 4).map((comp) => (
                            <CandidateRow
                              key={comp.sku}
                              comp={comp}
                              remote={espelhoRemotePricing[comp.sku]}
                              selected={espelhoSku === comp.sku}
                              onSelect={() => setEspelhoSku(espelhoSku === comp.sku ? null : comp.sku)}
                            />
                          ))}
                        </ul>

                        {/* Suggestion: closest priced espelho when top pick is custom-order */}
                        {espelhoSuggestion && (() => {
                          const suggPricing = pricingOf(espelhoSuggestion);
                          const note = extraKeyNote(boxes, boxesOf(espelhoSuggestion));
                          return (
                            <div className="mt-2 rounded-smartify border border-green-200 bg-green-50 p-3">
                              <p className="mb-1 text-xs font-semibold text-green-800">
                                Disponível já — alternativa imediata
                              </p>
                              <div className="flex items-center gap-2">
                                {espelhoSuggestion.imageUrl && (
                                  <img
                                    src={espelhoSuggestion.imageUrl}
                                    alt=""
                                    className="h-8 w-8 shrink-0 rounded border border-green-200 bg-white object-contain"
                                  />
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-smartify-ink">
                                    {friendlyName(espelhoSuggestion)}
                                  </p>
                                  <p className="text-xs text-smartify-muted">
                                    {formatPrice(suggPricing.priceEur)}
                                    {espelhoSuggestion.colorName ? ` · ${espelhoSuggestion.colorName}` : ''}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setEspelhoSku(espelhoSuggestion.sku)}
                                  className="shrink-0 rounded-smartify border border-green-300 bg-white px-2.5 py-1 text-xs font-semibold text-green-700 hover:bg-green-100"
                                >
                                  Usar este
                                </button>
                              </div>
                              {note && (
                                <p className="mt-1.5 text-xs text-smartify-muted">{note}</p>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — only shown in guided mode */}
        {mode === 'guided' && (
          <div className="border-t border-smartify-line p-4">
            {step < 3 ? (
              <button
                type="button"
                disabled={!canContinue}
                onClick={() => (step === 2 ? enterStep4() : setStep((s) => (s + 1) as 0 | 1 | 2 | 3))}
                className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-40"
              >
                Continuar →
              </button>
            ) : (
              <button
                type="button"
                disabled={!canContinue}
                onClick={handleSave}
                className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-40"
              >
                Guardar aparelho
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
